/* eslint-disable no-console */
import { useTranslation } from 'react-i18next';
import { NotificationsType } from 'src/api';
import {
  NotificationItem,
  type NotificationItemProps,
} from 'src/components/atomsLegacy/NotificationItem';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { NotificationsQuery } from 'src/graphql';
import { useIsMobile } from 'src/hooks/useIsMobile';
import { NavbarVariantEnum } from 'src/types';
import type { ArrayElement } from 'src/types/types';
import { isBefore } from 'src/utils/time';
import {
  getNotificationHelpLinkUrl,
  getNotificationIcon,
  getNotificationItemUrl,
  getNotificationSecondaryText,
  getNotificationSubtitle,
  getNotificationTitle,
  reportIdNotificationTypes,
} from './ActualNotifications.helpers';
import {
  ActualNotificationsContainer,
  ClearNotificationContainer,
  ClearNotificationsButton,
  NavbarDrawerTopOptions,
  NotificationsButton,
  StyledList,
} from './ActualNotifications.styles';
import { EmptyNotificationsComponent } from './EmptyNotificationsComponent';

interface ActualNotificationsProps {
  notifications: NotificationsQuery['notifications'];
  handleMenuClose: NotificationItemProps['handleMenuClose'];
  handleNotificationRead: NotificationItemProps['handleNotificationRead'];
  handleNotificationClose: NotificationItemProps['handleNotificationClose'];
  numberOfNotificationsToShow: number;
  notificationsOpen?: boolean;
  newNavEnabled?: boolean;
  showMore: () => void;
  clearAll: () => void;
  variant: NavbarVariantEnum;
  organisation?: string;
}

export const ActualNotifications = (props: ActualNotificationsProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  if (props.notifications.length === 0)
    return (
      <EmptyNotificationsComponent
        handleMenuClose={props.handleMenuClose}
        variant={props.variant}
      />
    );

  const renderNavbarDrawerTopOption = () => {
    if (
      isMobile &&
      (props.variant === NavbarVariantEnum.REPORTER ||
        props.variant === NavbarVariantEnum.PUBLIC)
    ) {
      return (
        <NavbarDrawerTopOptions>
          <StyledIcon
            className={`uil uil-angle-left`}
            size="32px"
            onClick={props.handleMenuClose}
            isClickable={true}
          />
          <ClearNotificationContainer>
            <ClearNotificationsButton
              newNavEnabled={props.newNavEnabled}
              notificationsOpen={props.notificationsOpen}
              data-testid={'clear-all-notifications'}
              onClick={props.clearAll}
            >
              {t('general.navbar.notificationsDrawer.markAllAsRead')}
            </ClearNotificationsButton>
          </ClearNotificationContainer>
        </NavbarDrawerTopOptions>
      );
    }
    return (
      <ClearNotificationContainer>
        <ClearNotificationsButton
          newNavEnabled={props.newNavEnabled}
          notificationsOpen={props.notificationsOpen}
          data-testid={'clear-all-notifications'}
          onClick={props.clearAll}
        >
          {t('general.navbar.notificationsDrawer.markAllAsRead')}
        </ClearNotificationsButton>
      </ClearNotificationContainer>
    );
  };
  return (
    <ActualNotificationsContainer data-testid="actual-notifications">
      {renderNavbarDrawerTopOption()}
      <StyledList>
        {props.notifications
          .sort((a, b) => {
            const aa = parseInt(a.createdAt);
            const bb = parseInt(b.createdAt);
            if (isBefore(aa, bb)) return 1;
            if (isBefore(bb, aa)) return -1;
            return 0;
          })
          .slice(0, props.numberOfNotificationsToShow)
          .map(
            (not: ArrayElement<NotificationsQuery['notifications']>, index) => {
              const caseOrReportId = reportIdNotificationTypes.includes(
                not.kind as NotificationsType,
              )
                ? not.report
                : not.case;
              const title = getNotificationTitle(not.kind);
              const secondaryText = getNotificationSecondaryText(
                not.kind,
                caseOrReportId,
                not.details,
              );
              const icon = getNotificationIcon(not.kind);
              const helpLinkUrl = getNotificationHelpLinkUrl(not.kind);
              const subtitle = getNotificationSubtitle(not.kind);

              return (
                <NotificationItem
                  title={title}
                  subtitle={subtitle}
                  secondaryText={secondaryText}
                  link={
                    props.variant === NavbarVariantEnum.MANAGER
                      ? getNotificationItemUrl({
                          variant: props.variant,
                          type: not.kind,
                          caseOrReportId: not.case,
                          importType: not.importType,
                        })
                      : getNotificationItemUrl({
                          variant: props.variant,
                          caseOrReportId: caseOrReportId,
                          organisation: props.organisation,
                          isOPRMessageNotification:
                            not.kind === NotificationsType.OPR_MESSAGE,
                          importType: not.importType,
                        })
                  }
                  notification={not}
                  key={not.id}
                  notificationsOpen={props.notificationsOpen}
                  newNavEnabled={props.newNavEnabled}
                  handleNotificationClose={props.handleNotificationClose}
                  handleMenuClose={props.handleMenuClose}
                  lastNotification={index === props.notifications.length - 1}
                  handleNotificationRead={props.handleNotificationRead}
                  icon={icon}
                  helpLinkUrl={helpLinkUrl}
                />
              );
            },
          )}
        {props.notifications.length > props.numberOfNotificationsToShow ? (
          <NotificationsButton onClick={props.showMore}>
            Show More
          </NotificationsButton>
        ) : null}
      </StyledList>
    </ActualNotificationsContainer>
  );
};
